import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import { useUser } from './UserContext';

const ModerationContext = createContext();

export const useModerationContext = () => {
  const context = useContext(ModerationContext);
  if (!context) {
    throw new Error('useModerationContext must be used within a ModerationProvider');
  }
  return context;
};

export const ModerationProvider = ({ children }) => {
  const [pendingQuestionsCount, setPendingQuestionsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useUser();

  // Solo los roles con permiso de moderación deben consultar el contador de pendientes.
  const puedeModerar = user?.rol === 'admin' || user?.rol === 'moderador';

  // Función para obtener el contador de preguntas pendientes
  const fetchPendingQuestionsCount = async () => {
    if (!puedeModerar) {
      return;
    }
    try {
      setIsLoading(true);
      // Pendientes de usuarios + propuestas del agente ('en_proceso'): el badge
      // de Revisión suma ambas ("preguntas por revisar").
      const [pendientesRes, agenteRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/preguntas_pendientes.php`),
        axios.get(`${API_BASE_URL}/revisiones_contador.php`),
      ]);
      let count = 0;
      if (pendientesRes.status === 'fulfilled' && Array.isArray(pendientesRes.value.data)) {
        count += pendientesRes.value.data.length;
      }
      if (agenteRes.status === 'fulfilled' && agenteRes.value.data?.success) {
        count += Number(agenteRes.value.data.en_proceso) || 0;
      }
      setPendingQuestionsCount(count);
      setError(null);
    } catch (err) {
      console.error('Error al obtener el contador de preguntas pendientes:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Inicializar y configurar polling SOLO para moderadores/administradores.
  useEffect(() => {
    if (!puedeModerar) {
      setPendingQuestionsCount(0);
      return undefined;
    }

    // Cargar el contador inmediatamente
    fetchPendingQuestionsCount();

    // Configurar polling cada 5 segundos para actualización en tiempo real
    const pollInterval = setInterval(() => {
      fetchPendingQuestionsCount();
    }, 5000);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeModerar]);

  const value = {
    pendingQuestionsCount,
    isLoading,
    error,
    fetchPendingQuestionsCount, // Exponer para actualización manual
  };

  return (
    <ModerationContext.Provider value={value}>
      {children}
    </ModerationContext.Provider>
  );
};
